'use client';

import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { z } from 'zod';

import { generalConfig } from '@sola/config/lib/getters.js';
import { programCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { GiftCatalogSortEnum } from '@sola/schema/lib/schema.js';

import { ArticleTabsEnum } from '../molecules/article-group/article-term-list';

const limitValues = ['4', '8', '12', '24'] as const;
export const ArticleLimitEnum = z.enum(limitValues);
export type ArticleLimitEnum = z.infer<typeof ArticleLimitEnum>;

const sortByValues = ['promotedContent', 'desc', 'asc'] as const;
export const ArticleSortByEnum = z.enum(sortByValues);
export type ArticleSortByEnum = z.infer<typeof ArticleSortByEnum>;

const sacFilterValues = [
  'orphanedChild',
  'hasDisabilities',
  'vulnerableExploitation',
  'affectedHiv',
] as const;
export const SacFiltersEnum = z.enum(sacFilterValues);
export type SacFiltersEnum = z.infer<typeof SacFiltersEnum>;

export const ProgramCountryIsoEnum = z.enum(programCountryIsoCodes);
export type ProgramCountryIsoEnum = z.infer<typeof ProgramCountryIsoEnum>;

export const usePaginationQueryParams = () => {
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(0).withOptions({ scroll: true }),
  );

  const [currentPageWithoutScroll, setCurrentPageWithoutScroll] = useQueryState(
    'page',
    parseAsInteger.withDefault(0),
  );

  return {
    currentPage,
    setCurrentPage,
    currentPageWithoutScroll,
    setCurrentPageWithoutScroll,
  };
};

export const useSearchQueryParams = () => {
  const [searchFilters, setSearchFilters] = useQueryStates({
    q: parseAsString.withDefault(''),
  });

  return { searchFilters, setSearchFilters };
};

export const useGiftCatalogQueryParams = () => {
  const [giftCatalogFilters, setGiftCatalogFilters] = useQueryStates({
    category: parseAsArrayOf(parseAsString, ',').withDefault([]),
    promotion: parseAsString.withDefault(''),
    priceMin: parseAsInteger.withDefault(generalConfig.catalogFilterMin),
    priceMax: parseAsInteger.withDefault(generalConfig.catalogFilterMax),
    sortby: parseAsString.withDefault(GiftCatalogSortEnum.enum.mostNeeded),
  });

  return {
    giftCatalogFilters,
    setGiftCatalogFilters,
  };
};

export const useTabGroupQueryParams = () => {
  const [tabGroupFilters, setTabGroupFilters] = useQueryStates({
    tab: parseAsString.withDefault(ArticleTabsEnum.enum.all),
  });

  return { tabGroupFilters, setTabGroupFilters };
};

export type ArticleQueryParams = {
  limit?: number;
  sortby?: ArticleSortByEnum;
};

export const useArticleQueryParams = (props?: { perPage?: number }) => {
  const { perPage } = props || {};
  const [articleFilters, setArticleFilters] = useQueryStates({
    limit: parseAsInteger.withDefault(perPage || 12),
    sortby: parseAsString.withDefault(ArticleSortByEnum.enum.desc),
  });

  return { articleFilters, setArticleFilters };
};

const { sacMinAge, sacMaxAge } = generalConfig;

export type SacQueryParams = {
  gender: ChildGenderEnum;
  countries: string[];
  ageMin: number;
  ageMax: number;
  birthDay: number;
  birthMonth: number;
  filters: string[];
};

export const useSacQueryParams = () => {
  const [sacFilters, setSacFilters] = useQueryStates({
    gender: parseAsStringEnum<ChildGenderEnum>(
      Object.values(ChildGenderEnum),
    ).withDefault(ChildGenderEnum.Either),
    countries: parseAsArrayOf(parseAsString.withDefault('')).withDefault([]),
    ageMin: parseAsInteger.withDefault(sacMinAge),
    ageMax: parseAsInteger.withDefault(sacMaxAge),
    birthDay: parseAsInteger.withDefault(0),
    birthMonth: parseAsInteger.withDefault(0),
    filters: parseAsArrayOf(parseAsString.withDefault(''), '%2C').withDefault(
      [],
    ),
  });

  return {
    sacFilters,
    setSacFilters,
  };
};
